/**
 * Menu — Radix `DropdownMenu` themed as a paper card identical to Popover.
 * Items are 36 px tall with hairline separators, mono uppercase eyebrow rows
 * for sections, and an accent-soft hover. Used for row-action menus, the user
 * dropdown that will host `<ThemeSwitcher />` later, and toolband overflows.
 *
 * Extrapolated within the design language — no dedicated artboard.
 */

import * as RadixMenu from '@radix-ui/react-dropdown-menu';
import type { ReactNode } from 'react';
import { Icon } from './Icon';

interface MenuProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  children: ReactNode;
}

export function Menu({ open, onOpenChange, defaultOpen, children }: MenuProps) {
  return (
    <RadixMenu.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {children}
    </RadixMenu.Root>
  );
}

interface MenuTriggerProps {
  children: ReactNode;
  asChild?: boolean;
  className?: string;
}

function Trigger({ children, asChild = true, className = '' }: MenuTriggerProps) {
  return (
    <RadixMenu.Trigger asChild={asChild} className={className}>
      {children}
    </RadixMenu.Trigger>
  );
}
Menu.Trigger = Trigger;

interface MenuContentProps {
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  /** Default min width matches toolband action chips. */
  minWidth?: number;
  className?: string;
}

function Content({
  children,
  side = 'bottom',
  align = 'end',
  sideOffset = 6,
  minWidth = 200,
  className = '',
}: MenuContentProps) {
  return (
    <RadixMenu.Portal>
      <RadixMenu.Content
        side={side}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={12}
        className={
          'z-50 max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto border border-line bg-paper py-1 text-ink shadow-[0_12px_32px_rgba(0,0,0,0.18)] focus:outline-none ' +
          'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 ' +
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 ' +
          'data-[side=top]:slide-in-from-bottom-1 data-[side=bottom]:slide-in-from-top-1 ' +
          'data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 ' +
          className
        }
        style={{ minWidth }}
      >
        {children}
      </RadixMenu.Content>
    </RadixMenu.Portal>
  );
}
Menu.Content = Content;

interface MenuLabelProps {
  children: ReactNode;
  className?: string;
}

function Label({ children, className = '' }: MenuLabelProps) {
  return (
    <RadixMenu.Label
      className={
        'select-none px-3 pt-2 pb-1 font-mono text-[10.5px] uppercase tracking-[0.06em] text-ink-3 ' +
        className
      }
    >
      {children}
    </RadixMenu.Label>
  );
}
Menu.Label = Label;

interface MenuItemProps {
  children: ReactNode;
  /** Optional icon glyph rendered before the label. */
  icon?: ReactNode;
  /** Optional kbd hint shown after the label. */
  shortcut?: ReactNode;
  /** Marks the row as destructive — switches to red ink. */
  danger?: boolean;
  disabled?: boolean;
  onSelect?: (event: Event) => void;
  className?: string;
}

function Item({
  children,
  icon,
  shortcut,
  danger,
  disabled,
  onSelect,
  className = '',
}: MenuItemProps) {
  return (
    <RadixMenu.Item
      disabled={disabled}
      onSelect={onSelect}
      className={[
        'flex h-9 cursor-pointer select-none items-center gap-2.5 px-3 text-[13px] leading-tight outline-none transition-colors',
        'data-[highlighted]:bg-accent-soft data-[highlighted]:text-ink',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        danger ? 'text-red-text data-[highlighted]:text-red-text' : 'text-ink',
        className,
      ].join(' ')}
    >
      {icon != null && (
        <span className="grid size-4 place-items-center text-ink-3">{icon}</span>
      )}
      <span className="flex-1">{children}</span>
      {shortcut != null && (
        <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.06em] text-ink-3">
          {shortcut}
        </span>
      )}
    </RadixMenu.Item>
  );
}
Menu.Item = Item;

interface MenuCheckItemProps {
  children: ReactNode;
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /** `event.preventDefault()` here keeps the menu open (multi-toggle menus). */
  onSelect?: (event: Event) => void;
  shortcut?: ReactNode;
  disabled?: boolean;
}

function CheckItem({
  children,
  checked,
  onCheckedChange,
  onSelect,
  shortcut,
  disabled,
}: MenuCheckItemProps) {
  return (
    <RadixMenu.CheckboxItem
      checked={checked}
      onCheckedChange={onCheckedChange}
      onSelect={onSelect}
      disabled={disabled}
      className={[
        'flex h-9 cursor-pointer select-none items-center gap-2.5 px-3 pl-8 text-[13px] leading-tight outline-none transition-colors',
        'data-[highlighted]:bg-accent-soft',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        'relative text-ink',
      ].join(' ')}
    >
      <RadixMenu.ItemIndicator className="absolute left-3 grid size-4 place-items-center text-accent-text">
        <Icon name="check" size={12} />
      </RadixMenu.ItemIndicator>
      <span className="flex-1">{children}</span>
      {shortcut != null && (
        <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.06em] text-ink-3">
          {shortcut}
        </span>
      )}
    </RadixMenu.CheckboxItem>
  );
}
Menu.CheckItem = CheckItem;

function Separator({ className = '' }: { className?: string }) {
  return (
    <RadixMenu.Separator className={'my-1 h-px bg-line ' + className} />
  );
}
Menu.Separator = Separator;
